// @ts-check

/**
 * @file Shared task detail sub-navigation.
 */

import FontAwesome6 from "@expo/vector-icons/FontAwesome6"
import _ from "gettext-universal/build/src/translate.js"
import useLocale from "gettext-universal/build/src/use-locale-expo.js"
import PropTypes from "prop-types"
import propTypesExact from "prop-types-exact"
import {memo} from "react"
import {View} from "react-native-propforge"
import {shapeComponent, ShapeComponent} from "set-state-compare/build/shape-component.js"

import buildRoutePath from "../../shared/build-route-path"
import Button from "../../components/button"
import Text from "@kaspernj/api-maker/build/utils/text"
import useThemeColors, {useThemeName} from "../../shared/use-theme-colors"

/** @import {NamedExoticComponent, ReactNode} from "react" */
/** @typedef {import("react").ComponentProps<typeof FontAwesome6>["name"]} FontAwesome6Name */
/** @typedef {"assignedUsers" | "comments" | "general" | "timelogs"} TaskSubnavItemKey */
/** @typedef {Record<string, string | string[] | undefined>} TaskSubnavRouteParams */

/** @type {Record<string, object>} */
const styles = {}

/**
 * @typedef {object} TaskSubnavItemProps
 * @property {boolean} active Whether this nav item is the active item.
 * @property {string} href Destination route.
 * @property {FontAwesome6Name} iconName FontAwesome icon name.
 * @property {string} label Item label.
 * @property {string} testID Stable test id.
 */

/** @typedef {Record<string, never>} TaskSubnavItemState */

/**
 * Renders one task subnav item.
 * @augments {ShapeComponent<TaskSubnavItemProps, TaskSubnavItemState>}
 */
class TaskSubnavItem extends ShapeComponent {
  static propTypes = propTypesExact({
    active: PropTypes.bool.isRequired,
    href: PropTypes.string.isRequired,
    iconName: PropTypes.string.isRequired,
    label: PropTypes.string.isRequired,
    testID: PropTypes.string.isRequired
  })

  /** @type {ReturnType<typeof useThemeColors>} */
  colors = this.hookField()
  /** @type {ReturnType<typeof useThemeName>} */
  themeName = this.hookField()

  /** Wires the active theme palette into the instance. */
  setup() {
    this.colors = useThemeColors()
    this.themeName = useThemeName()
  }

  /**
   * Renders an active item or link button.
   * @returns {ReactNode} Item content.
   */
  render() {
    const {active, href, iconName, label, testID} = this.p
    const colors = this.tt.colors
    const themeName = this.tt.themeName
    if (!active) {
      return (
        <Button
          href={href}
          iconName={iconName}
          label={label}
          testID={testID}
          variant="outline"
        />
      )
    }
    return (
      <View
        style={styles[`activeItem-${themeName}`] ||= {
          alignItems: "center",
          backgroundColor: colors.infoBackground,
          borderColor: colors.info,
          borderRadius: 10,
          borderWidth: 1,
          flexDirection: "row",
          paddingHorizontal: 14,
          paddingVertical: 10
        }}
        testID={testID}
      >
        <View
          style={styles.activeIcon ||= {
            alignItems: "center",
            justifyContent: "center",
            marginRight: 8,
            width: 16
          }}
          testID={`${testID}/icon`}
        >
          <FontAwesome6 color={colors.link} name={iconName} size={14} />
        </View>
        <Text
          style={styles[`activeText-${themeName}`] ||= {
            color: colors.link,
            fontSize: 14,
            fontWeight: 700
          }}
          testID={`${testID}/label`}
        >
          {label}
        </Text>
      </View>
    )
  }
}

/** @type {NamedExoticComponent<TaskSubnavItemProps>} */
const MemoizedTaskSubnavItem = memo(shapeComponent(TaskSubnavItem))

/**
 * @typedef {object} TaskSubnavProps
 * @property {TaskSubnavItemKey} activeItem Active subnav item.
 * @property {number} assignedUsersCount Number shown beside the assigned-users tab.
 * @property {number} commentsCount Number shown beside the comments tab.
 * @property {TaskSubnavRouteParams} params Current route params.
 * @property {string} [projectId] Owning project id when the current URL does not carry one.
 * @property {string} taskId Current task id.
 * @property {string} testID Stable test id for the subnav root.
 * @property {number} timelogsCount Number shown beside the timelogs tab.
 */

/** @typedef {Record<string, never>} TaskSubnavState */

/**
 * Shared task detail sub-navigation.
 * @augments {ShapeComponent<TaskSubnavProps, TaskSubnavState>}
 */
class TaskSubnav extends ShapeComponent {
  static propTypes = propTypesExact({
    activeItem: PropTypes.oneOf(["assignedUsers", "comments", "general", "timelogs"]).isRequired,
    assignedUsersCount: PropTypes.number.isRequired,
    commentsCount: PropTypes.number.isRequired,
    params: PropTypes.object.isRequired,
    projectId: PropTypes.string,
    taskId: PropTypes.string.isRequired,
    testID: PropTypes.string.isRequired,
    timelogsCount: PropTypes.number.isRequired
  })

  /** Initializes translations for labels. */
  setup() {
    useLocale()
  }

  /**
   * Renders the task subnav.
   * @returns {ReactNode} Subnav content.
   */
  render() {
    const {activeItem, assignedUsersCount, commentsCount, params, projectId, taskId, testID, timelogsCount} = this.p
    const routeProjectId = Array.isArray(params.projectId) ? params.projectId[0] : params.projectId
    const projectID = routeProjectId || projectId
    const projectQuery = projectID ? `?projectId=${encodeURIComponent(projectID)}` : ""
    return (
      <View
        style={styles.container ||= {
          flexDirection: "row",
          flexWrap: "wrap",
          gap: 8,
          marginBottom: 16
        }}
        testID={testID}
      >
        <MemoizedTaskSubnavItem
          active={activeItem === "general"}
          href={buildRoutePath(params, `/tasks/${taskId}${projectQuery}`)}
          iconName="circle-info"
          label={_("General")}
          testID={`${testID}/generalLink`}
        />
        <MemoizedTaskSubnavItem
          active={activeItem === "comments"}
          href={buildRoutePath(params, `/tasks/${taskId}/comments${projectQuery}`)}
          iconName="comments"
          label={labelWithCount(_("Comments"), commentsCount)}
          testID={`${testID}/commentsLink`}
        />
        <MemoizedTaskSubnavItem
          active={activeItem === "timelogs"}
          href={buildRoutePath(params, `/tasks/${taskId}/timelogs${projectQuery}`)}
          iconName="clock"
          label={labelWithCount(_("Timelogs"), timelogsCount)}
          testID={`${testID}/timelogsLink`}
        />
        <MemoizedTaskSubnavItem
          active={activeItem === "assignedUsers"}
          href={buildRoutePath(params, `/tasks/${taskId}/assigned-users${projectQuery}`)}
          iconName="users"
          label={labelWithCount(_("Assigned users"), assignedUsersCount)}
          testID={`${testID}/assignedUsersLink`}
        />
      </View>
    )
  }
}

/**
 * Appends a count when one is available.
 * @param {string} label Base label.
 * @param {number} count Count to append.
 * @returns {string} Display label.
 */
function labelWithCount(label, count) {
  return `${label} (${count})`
}

/** @type {NamedExoticComponent<TaskSubnavProps>} */
const TaskSubnavComponent = memo(shapeComponent(TaskSubnav))

export default TaskSubnavComponent
